/**
 * DAC profiles -- group 05 (zero-feedback, ladder and FPGA converters).
 *
 * This group is deliberately weighted away from chip-and-op-amp practice. Most
 * of these designs either compute their own reconstruction in an FPGA, convert
 * with a discrete resistor ladder, or dispense with an active output stage
 * altogether, so the parameters that matter are ladder linearity, output
 * impedance and reconstruction behaviour rather than a headline THD+N number.
 * Several of these manufacturers publish no distortion figure at all; where
 * that is the case the entry is tagged ESTIMATED and the provenance note says
 * so explicitly. Nothing here is a measurement of an individual unit.
 */

import type { DacProfile } from '../../types/hardware';
import {
  FILTERS,
  HARMONIC_SHAPES,
  TRANSFORMER_LINE,
  TUBE_LINE,
  makeDac,
  usd,
} from '../kit';

export const DACS_05: DacProfile[] = [
  makeDac({
    mfr: 'Ayre Acoustics',
    model: 'QX-5 Twenty',
    price: usd(8950, 'MSRP', 2016, 'US retail at introduction for the digital hub version.'),
    year: 2016,
    country: 'United States',
    architecture: 'delta-sigma',
    core: 'ESS Sabre delta-sigma core, fully balanced, feeding the Ayre Diamond discrete output',
    outputStage: 'discrete-class-a',
    dynamicRangeDb: 120,
    thdPlusNDb: -108,
    crosstalkDb: -122,
    outputVrmsSE: 2.0,
    outputVrmsBal: 4.0,
    outputImpedanceOhms: 300,
    balanced: true,
    dsd: 'dsd128',
    filters: [
      { ...FILTERS.sharpMinimum(64), id: 'listen', label: 'Listen (minimum phase, short)' },
      { ...FILTERS.sharpLinear(128), id: 'measure', label: 'Measure (linear phase)' },
    ],
    defaultFilterId: 'listen',
    jitterPsRms: 25,
    noiseShaperOrder: 5,
    idleToneLevel: 0.02,
    effectiveBits: 20,
    confidence: 'ESTIMATED',
    accent: '#6fbfd6',
    tags: ['zero-feedback', 'fully-balanced', 'diamond-output'],
    notes:
      'A conventional delta-sigma converter followed by a zero-feedback, fully balanced discrete output stage, which is where the electrical character of this design actually lives. Removing the global loop raises source impedance to a few hundred ohms and leaves a small, level-dependent low-order residue instead of the flat sub-ppm floor a feedback op-amp buffer would give. The two selectable filters are genuinely different in the time domain rather than in the passband: the default minimum-phase filter has essentially no pre-ringing, and the linear-phase alternative trades that for symmetric impulse behaviour.',
    provenanceNote:
      'Output configuration, filter naming and the zero-feedback discrete output topology come from Ayre published material. Dynamic range and THD+N are ESTIMATED from class norms: Ayre does not publish a distortion figure for this product, and the specific converter part number is not confirmed here, so the core string describes the family rather than a verified part.',
  }),

  makeDac({
    mfr: 'PS Audio',
    model: 'DirectStream DAC',
    price: usd(5999, 'ORIGINAL_RETAIL', 2014, 'US retail at launch; superseded by the MK2 and no longer in production.'),
    year: 2014,
    country: 'United States',
    architecture: 'fpga-custom',
    core: 'Ted Smith FPGA: all inputs converted to high-rate single-bit DSD, passive analog reconstruction',
    outputStage: 'transformer',
    dynamicRangeDb: 117,
    thdPlusNDb: -100,
    crosstalkDb: -108,
    outputVrmsSE: 2.8,
    outputVrmsBal: 5.6,
    outputImpedanceOhms: 100,
    balanced: true,
    dsd: 'dsd128',
    filters: [
      {
        ...FILTERS.hybrid(2048),
        id: 'ds-fixed',
        label: 'Fixed FPGA upsample to high-rate DSD',
        cutoffRatio: 0.92,
        transitionRatio: 0.1,
        stopbandDb: -110,
      },
    ],
    internalOversampling: 20,
    jitterPsRms: 30,
    effectiveBits: 19,
    response: TRANSFORMER_LINE(8, 48000),
    confidence: 'ESTIMATED',
    accent: '#a0d468',
    tags: ['fpga', 'dsd-native', 'passive-filter', 'transformer-out'],
    notes:
      'Every input, PCM or DSD, is re-modulated in the FPGA to a single-bit high-rate stream and then reconstructed passively, so there is no selectable filter and no conventional analog gain stage in the signal path. The audible consequence modelled here is the output transformer: a first-order low-frequency corner in the single digits of hertz, a soft high-frequency limit, and core hysteresis that adds a small second-harmonic term which grows as level and falls with frequency. Because the modulator is fixed, the reconstruction ringing signature does not change with the sample rate presented at the input.',
    provenanceNote:
      'Architecture, output voltages and the transformer-coupled passive output are from PS Audio published descriptions of the Ted Smith design. Dynamic range and THD+N are ESTIMATED: PS Audio does not publish a full distortion specification for this converter. Transformer corner frequencies are modelled, not quoted.',
  }),

  makeDac({
    mfr: 'Rockna Audio',
    model: 'Wavedream Signature',
    price: usd(12500, 'APPROXIMATE', 2023, 'Approximate US retail; Rockna prices vary by distributor and configuration, and this figure is not verified.'),
    year: 2018,
    country: 'Romania',
    architecture: 'hybrid',
    core: 'FPGA oversampling engine driving a discrete R-2R ladder per phase, dual mono',
    outputStage: 'op-amp',
    dynamicRangeDb: 124,
    thdPlusNDb: -110,
    crosstalkDb: -128,
    outputVrmsSE: 2.5,
    outputVrmsBal: 5.0,
    outputImpedanceOhms: 22,
    balanced: true,
    dsd: 'dsd256',
    filters: [
      FILTERS.sharpLinear(128),
      FILTERS.apodizing(128),
      FILTERS.sharpMinimum(96),
      FILTERS.nosCompensated(),
    ],
    defaultFilterId: 'sharp-linear',
    ladderMismatchLsb: 0.25,
    effectiveBits: 21,
    internalOversampling: 16,
    jitterPsRms: 18,
    harmonicShape: HARMONIC_SHAPES.r2r,
    nonlinear: { kind: 'r2r-inl', inlLsb: 0.25, amount: 0.45, asymmetry: 0.22, headroomDb: 8 },
    confidence: 'ESTIMATED',
    accent: '#7f9cf5',
    tags: ['fpga', 'r2r', 'selectable-filters', 'dual-mono'],
    notes:
      'A split design: the interpolation is computed in an FPGA and the conversion is done by a discrete ladder, so it behaves like a ladder in the analog domain but like an oversampling converter in the time domain. Ladder trimming is tight enough that the level-dependent INL term is modelled at a quarter of an LSB, roughly half that of a typical unmatched discrete ladder, which keeps the low-level distortion tail from dominating. The low-impedance buffered output means the following preamplifier input impedance has almost no effect on the response, unlike the passive-output ladders elsewhere in this group.',
    provenanceNote:
      'Architecture, dual-mono layout and the selectable filter set are from Rockna published descriptions. Dynamic range, THD+N, output impedance and ladder mismatch are ESTIMATED from class norms; Rockna does not publish a full electrical specification, and the price is an unverified approximation.',
  }),

  makeDac({
    mfr: 'Aqua Acoustic Quality',
    model: 'La Scala MkII Optologic',
    price: usd(7400, 'APPROXIMATE', 2022, 'Approximate US retail; Aqua is priced in EUR and dealer pricing varies, so this figure is not verified.'),
    year: 2017,
    country: 'Italy',
    architecture: 'ladder-multibit',
    core: 'Multibit ladder conversion, no oversampling, optically isolated Optologic digital-to-analog interface',
    outputStage: 'tube',
    dynamicRangeDb: 110,
    thdPlusNDb: -80,
    crosstalkDb: -84,
    outputVrmsSE: 2.5,
    outputVrmsBal: 5.0,
    outputImpedanceOhms: 900,
    balanced: true,
    dsd: 'dsd64',
    sampleRates: [44100, 48000, 88200, 96000, 176400, 192000, 352800, 384000],
    maxBitDepth: 24,
    filters: [FILTERS.nos()],
    ladderMismatchLsb: 0.6,
    effectiveBits: 18,
    jitterPsRms: 80,
    harmonicShape: HARMONIC_SHAPES.tubeDac,
    response: TUBE_LINE(10, -1.3),
    slewRateVus: 14,
    confidence: 'ESTIMATED',
    accent: '#e2854f',
    tags: ['nos', 'ladder', 'tube-output', 'galvanic-isolation'],
    notes:
      'Non-oversampling multibit conversion into a triode output stage with no global feedback, which stacks two independent signatures. The zero-order hold imposes the full sin(x)/x envelope, about -3.9 dB at Nyquist at 44.1 kHz with no compensation, and image energy above Nyquist is left for the analog stage to deal with; on top of that the triode stage contributes a strongly second-harmonic-biased, asymmetric transfer curve whose distortion rises roughly a decibel per decibel of drive. The 900 ohm source impedance is high enough that a low-impedance or capacitive interconnect load will measurably alter the top octave.',
    provenanceNote:
      'Non-oversampling ladder topology, optical isolation of the digital interface and the valve output stage are from Aqua published descriptions. Dynamic range, THD+N, output impedance and ladder mismatch are all ESTIMATED: Aqua publishes no distortion or dynamic range figure, which is normal for no-feedback valve output designs. The sin(x)/x droop is computed, not invented.',
  }),

  makeDac({
    mfr: 'Totaldac',
    model: 'd1-core',
    price: usd(6000, 'APPROXIMATE', 2023, 'Approximate USD equivalent of the EUR list price; Totaldac sells direct in EUR, so this conversion is not a verified US MSRP.'),
    year: 2016,
    country: 'France',
    architecture: 'r2r',
    core: 'Discrete R-2R ladder built from bulk metal foil resistors, FPGA-computed filtering',
    outputStage: 'passive-iv',
    dynamicRangeDb: 112,
    thdPlusNDb: -86,
    crosstalkDb: -98,
    outputVrmsSE: 2.0,
    outputImpedanceOhms: 470,
    balanced: false,
    dsd: 'dsd64',
    sampleRates: [44100, 48000, 88200, 96000, 176400, 192000],
    maxBitDepth: 24,
    filters: [FILTERS.nosCompensated(), FILTERS.sharpLinear(128)],
    defaultFilterId: 'nos-comp',
    ladderMismatchLsb: 0.3,
    effectiveBits: 18,
    jitterPsRms: 55,
    confidence: 'ESTIMATED',
    accent: '#c8b6a6',
    tags: ['r2r', 'nos', 'passive-output', 'foil-resistors'],
    notes:
      'The ladder output is taken directly, with no active current-to-voltage or buffer stage between the resistor network and the output socket. That is the defining electrical fact: the source impedance is set by the ladder itself, so the load presented by the next component forms a divider with it and can shave the top octave, and there is no op-amp or valve nonlinearity layered on top of the ladder INL. Close-tolerance foil resistors keep that INL term near a third of an LSB, so the residual distortion is low-order and does not collapse away at low signal level the way a delta-sigma noise floor does.',
    provenanceNote:
      'Ladder construction, foil resistor selection and the direct passive output are from Totaldac published descriptions. Output level, output impedance, dynamic range and THD+N are ESTIMATED; Totaldac does not publish a conventional distortion specification. Price is a currency conversion estimate, not a verified US MSRP.',
  }),

  makeDac({
    mfr: 'Metrum Acoustics',
    model: 'Pavane',
    price: usd(4500, 'APPROXIMATE', 2019, 'Approximate original retail; Metrum Acoustics has ceased trading, so this is a historic figure and is not verified.'),
    year: 2016,
    country: 'Netherlands',
    architecture: 'ladder-multibit',
    core: 'Four proprietary Transient multibit ladder modules, non-oversampling, dual differential',
    outputStage: 'jfet',
    dynamicRangeDb: 114,
    thdPlusNDb: -94,
    crosstalkDb: -104,
    outputVrmsSE: 2.0,
    outputVrmsBal: 4.0,
    outputImpedanceOhms: 100,
    balanced: true,
    dsd: 'none',
    sampleRates: [44100, 48000, 88200, 96000, 176400, 192000, 352800, 384000],
    maxBitDepth: 24,
    filters: [FILTERS.nos(), FILTERS.slowLinear(48)],
    defaultFilterId: 'nos',
    ladderMismatchLsb: 0.45,
    effectiveBits: 19,
    jitterPsRms: 70,
    confidence: 'ESTIMATED',
    accent: '#8fa8b8',
    tags: ['nos', 'multibit', 'dual-differential', 'pcm-only'],
    notes:
      'Four ladder modules run in a dual differential arrangement, two per channel in opposite polarity, which cancels the even-order component of the module nonlinearity and leaves a residue that is more odd-order than a single-ended ladder of the same INL. There is no digital filter in the default path, so the uncorrected zero-order hold droop and the mirror images above Nyquist are both present. A discrete FET buffer keeps output impedance around 100 ohms, so unlike the passive-output ladders here the load does not modify the response.',
    provenanceNote:
      'Module count, dual differential arrangement and the non-oversampling PCM-only signal path are from Metrum published material. Dynamic range, THD+N, output impedance and ladder mismatch are ESTIMATED; Metrum published very little electrical data, and the discrete FET output description is inferred from the design family rather than a datasheet.',
  }),

  makeDac({
    mfr: 'Sonnet Digital Audio',
    model: 'Pasithea',
    price: usd(8500, 'APPROXIMATE', 2023, 'Approximate US retail converted from EUR list; not a verified US MSRP.'),
    year: 2021,
    country: 'Netherlands',
    architecture: 'discrete-r2r',
    core: 'Fully discrete balanced R-2R ladder, four ladders per channel, non-oversampling',
    outputStage: 'discrete-class-a',
    dynamicRangeDb: 118,
    thdPlusNDb: -100,
    crosstalkDb: -112,
    outputVrmsSE: 2.5,
    outputVrmsBal: 5.0,
    outputImpedanceOhms: 47,
    balanced: true,
    dsd: 'dsd128',
    filters: [FILTERS.nos(), FILTERS.slowMinimum(32)],
    defaultFilterId: 'nos',
    ladderMismatchLsb: 0.35,
    effectiveBits: 19,
    jitterPsRms: 35,
    confidence: 'ESTIMATED',
    accent: '#b48ead',
    tags: ['discrete-r2r', 'nos', 'balanced-ladder', 'class-a-output'],
    notes:
      'A balanced discrete ladder per channel with a low-impedance class A buffer, which places it between the passive-output ladders and the buffered FPGA designs in this group. Running the ladder balanced cancels the even-order share of the element mismatch, so the modelled INL residue is odd-weighted and roughly a third of an LSB rather than the near-unity mismatch of a legacy multibit chip. Because it is non-oversampling by default it still carries the full sin(x)/x droop and unfiltered images, but the 47 ohm output makes it immune to interconnect and input-impedance loading.',
    provenanceNote:
      'Discrete balanced ladder construction and non-oversampling operation are from Sonnet published descriptions. Dynamic range, THD+N, output impedance and ladder mismatch are ESTIMATED from class norms; Sonnet does not publish a distortion specification. Price is a currency conversion estimate.',
  }),

  makeDac({
    mfr: 'Border Patrol',
    model: 'DAC SE-i',
    price: usd(2400, 'APPROXIMATE', 2022, 'Approximate US retail for the SE-i with the internal power supply upgrade; not verified.'),
    year: 2018,
    country: 'United States',
    architecture: 'ladder-multibit',
    core: 'Philips TDA1543 multibit ladder, non-oversampling, valve-rectified choke-input supply',
    outputStage: 'transformer',
    dynamicRangeDb: 100,
    thdPlusNDb: -72,
    crosstalkDb: -78,
    outputVrmsSE: 2.2,
    outputImpedanceOhms: 600,
    balanced: false,
    dsd: 'none',
    sampleRates: [44100, 48000, 88200, 96000, 176400, 192000],
    maxBitDepth: 24,
    filters: [FILTERS.nos()],
    ladderMismatchLsb: 1.2,
    effectiveBits: 16,
    jitterPsRms: 200,
    response: TRANSFORMER_LINE(14, 28000),
    confidence: 'ESTIMATED',
    accent: '#d0906a',
    tags: ['nos', 'tda1543', 'valve-rectified', 'transformer-out', '16-bit'],
    notes:
      'A deliberately minimal 16-bit multibit converter with no oversampling, no digital filter and no reclocking, coupled out through a transformer. Everything that a modern converter suppresses is left in place here: ladder mismatch above one LSB puts a level-dependent nonlinearity floor around 100 dB down that does not fall with signal level, incoming interface jitter is passed rather than reclocked, and the transformer restricts bandwidth at both ends. The result is the widest-signature entry in this group and the natural counterexample to the buffered FPGA designs above.',
    provenanceNote:
      'Converter chip, non-oversampling operation, valve-rectified choke-input power supply and transformer-coupled output are from Border Patrol published descriptions. Dynamic range, THD+N, output impedance, jitter and ladder mismatch are ESTIMATED: Border Patrol publishes no measured specifications, though the 16-bit effective resolution follows directly from the converter chip used. Price is approximate.',
  }),
];
